import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile';
  keywords?: string[];
  author?: string;
  jsonLd?: object;
  noindex?: boolean;
}

/**
 * SEOHead component for managing meta tags
 * Uses native DOM manipulation (React 19 compatible)
 */
const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  image,
  url,
  type = 'website',
  keywords = [],
  author,
  jsonLd,
  noindex = false
}) => {
  useEffect(() => {
    // Set document title
    const fullTitle = `${title} | ClickCollab`;
    document.title = fullTitle;

    // Helper to update or create meta tags
    const updateMeta = (selector: string, content: string, attrName: string = 'name') => {
      let element = document.querySelector(selector) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        if (attrName === 'property') {
          element.setAttribute('property', selector.replace('meta[property="', '').replace('"]', ''));
        } else {
          element.setAttribute('name', selector.replace('meta[name="', '').replace('"]', ''));
        }
        document.head.appendChild(element);
      }
      element.content = content;
    };

    // Robots meta tag (prevent Soft 404s for error/not-found states)
    if (noindex) {
      let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
      if (!robots) {
        robots = document.createElement('meta');
        robots.name = 'robots';
        document.head.appendChild(robots);
      }
      robots.content = 'noindex, nofollow';
    } else {
      let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
      if (robots) {
        robots.content = 'index, follow';
      }
    }

    // Basic meta tags
    updateMeta('meta[name="description"]', description);
    if (keywords.length > 0) {
      updateMeta('meta[name="keywords"]', keywords.join(', '));
    }
    if (author) {
      updateMeta('meta[name="author"]', author);
    }

    // Open Graph tags
    updateMeta('meta[property="og:title"]', fullTitle, 'property');
    updateMeta('meta[property="og:description"]', description, 'property');
    updateMeta('meta[property="og:type"]', type, 'property');
    
    // Canonical & OG URL
    const canonicalUrl = url || `${window.location.origin}${window.location.pathname}`;
    updateMeta('meta[property="og:url"]', canonicalUrl, 'property');
    
    if (image) {
      updateMeta('meta[property="og:image"]', image, 'property');
    }
    updateMeta('meta[property="og:site_name"]', 'ClickCollab', 'property');

    // Twitter Card tags
    updateMeta('meta[name="twitter:card"]', image ? 'summary_large_image' : 'summary');
    updateMeta('meta[name="twitter:title"]', fullTitle);
    updateMeta('meta[name="twitter:description"]', description);
    if (image) {
      updateMeta('meta[name="twitter:image"]', image);
    }

    // Canonical Link tag
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    // JSON-LD Structured Data
    if (jsonLd) {
      const scriptId = 'seo-jsonld';
      let script = document.getElementById(scriptId) as HTMLScriptElement;
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    }

    // Cleanup function
    return () => {
      // Optionally reset to defaults on unmount
      // document.title = 'ClickCollab';
    };
  }, [title, description, image, url, type, keywords, author, jsonLd, noindex]);

  return null; // This component doesn't render anything
};

export default SEOHead;
